import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";

// The single adaptive contract: navigation, hubs, entitlements, onboarding and one-time
// orientation state all flow through this context. See /app/memory/PLANNIO_ARCHITECTURE_DIAGRAM.md.
const ExperienceContext = createContext(null);

export function ExperienceProvider({ children }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    // No cache-buster needed: every mutation (e.g. completing onboarding) clears the whole GET
    // micro-cache, so a plain GET here is always fresh after any user action, and it lets the
    // session-boot primed payload serve the first render without a network request.
    api.get("/experience").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setHub = useCallback(async (hubId, action, order) => {
    // Optimistic update; the payload is the single source for nav in Phase 1+.
    setData((d) => {
      if (!d) return d;
      const enabled = new Set(d.hubs.enabled);
      if (action === "enable") enabled.add(hubId);
      if (action === "hide") enabled.delete(hubId);
      const next = action === "reorder" ? order.filter((h) => enabled.has(h)) : [...enabled];
      return { ...d, hubs: { ...d.hubs, enabled: next } };
    });
    try {
      await api.put(`/experience/hubs/${hubId}`, { action, order });
    } catch (e) {
      load();
      throw e;
    }
  }, [load]);

  const markFirstRun = useCallback(async (key) => {
    // Optimistic: dismissing the intro modal reveals the quiet banner; completing hides both.
    setData((d) => {
      if (!d) return d;
      const done = key === "v2_intro_done";
      return { ...d, v2_intro: { show_modal: false, show_banner: !done && key === "v2_intro" } };
    });
    try { await api.post(`/experience/first-run/${key}`); } catch {}
  }, []);

  return (
    <ExperienceContext.Provider value={{ experience: data, reload: load, setHub, markFirstRun }}>
      {children}
    </ExperienceContext.Provider>
  );
}

export function useExperience() {
  return useContext(ExperienceContext) || { experience: null, reload: () => {}, setHub: () => {}, markFirstRun: () => {} };
}

export function useCapability(key) {
  const { experience } = useExperience();
  if (!experience) return { available: undefined, loading: true };
  return { available: !!experience.entitlements?.[key], loading: false };
}

// Gate a premium capability. Renders children when available, `fallback` when not,
// nothing while loading. Usage: <CapabilityGate capability="travel.trips" fallback={<UpgradePanel/>}>
export function CapabilityGate({ capability, fallback = null, children }) {
  const { available, loading } = useCapability(capability);
  if (loading) return null;
  return available ? children : fallback;
}
